import { useCallback, useState } from 'react';
import { resolveBand, BANDS, type BandId } from '@/domain/bands';
import { countWithin, rankStations, type Coords, type Station, type StationWithDistance } from '@/domain/geo';
import { geocodeCep as defaultGeocode, type GeocodeResult } from '@/services/geocoding';
import { track } from '@/services/analytics';

export type LookupResult = {
  band: BandId;
  nearest: StationWithDistance;
  withinCount: number;
  withinRadiusKm: number;
  coords: Coords;
  label: string;
  cep: string | null;
};

export type LookupErrorKind =
  | 'invalid_cep'
  | 'cep_not_found'
  | 'imprecise'
  | 'unavailable'
  | 'geolocation_denied'
  | 'geolocation_unavailable'
  | 'no_stations';

export type LookupState = 'idle' | 'busy' | 'ready' | 'error';

type UseLookupParams = {
  stations: readonly Station[];
  geocode?: (cep: string) => Promise<GeocodeResult>;
  geolocate?: () => Promise<Coords>;
};

const PERMISSION_DENIED = 1;

/* Five decimals is about one metre, which is finer than the device itself reports.
   It exists to stop a float such as -23.550412345678901 from reaching analytics as
   seventeen digits of false precision. */
const COORD_DECIMALS = 5;

const roundCoord = (value: number): number => {
  const factor = 10 ** COORD_DECIMALS;
  return Math.round(value * factor) / factor;
};

/* The spec's GeolocationPositionError is not a global in jsdom, so `instanceof`
   would throw during tests. Recognise the shape the callback actually rejects with,
   and treat anything unrecognisable as a denial: that is the message that tells the
   lead what to do next. */
const isPositionError = (value: unknown): value is { code: number } =>
  value instanceof Object && 'code' in value && typeof value.code === 'number';

const browserGeolocate = (): Promise<Coords> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
      (error) => reject(error),
      { timeout: 10000, maximumAge: 60000 },
    );
  });

export const useLookup = ({
  stations,
  geocode = defaultGeocode,
  geolocate = browserGeolocate,
}: UseLookupParams) => {
  const [state, setState] = useState<LookupState>('idle');
  const [result, setResult] = useState<LookupResult | null>(null);
  const [errorKind, setErrorKind] = useState<LookupErrorKind | null>(null);

  const fail = useCallback((kind: LookupErrorKind) => {
    setResult(null);
    setErrorKind(kind);
    setState('error');
  }, []);

  const succeed = useCallback(
    (coords: Coords, label: string, cep: string | null) => {
      const ranked = rankStations(coords, stations);
      const nearest = ranked[0];
      if (!nearest) {
        fail('no_stations');
        return;
      }
      const band = resolveBand(nearest.distanceKm);
      setErrorKind(null);
      setResult({
        band,
        nearest,
        withinCount: countWithin(ranked, BANDS.limitedFromKm),
        withinRadiusKm: BANDS.limitedFromKm,
        coords,
        label,
        cep,
      });
      setState('ready');
      track('sf_band_shown', { band, source: cep === null ? 'geolocation' : 'cep' });
    },
    [fail, stations],
  );

  const submitCep = useCallback(
    async (cep: string) => {
      setState('busy');
      /* Emitted on submission, not on success: a CEP the geocoder cannot resolve is
         still a lead who asked, and where those land says where coverage is thin. */
      track('sf_location_submitted', { input_type: 'cep', cep });
      const geocoded = await geocode(cep);
      if (geocoded.status !== 'ok') {
        if (geocoded.status === 'imprecise') {
          track('sf_geocode_degraded', { reason: 'imprecise' });
        }
        fail(geocoded.status);
        return;
      }
      const place = [geocoded.neighborhood, geocoded.city].filter((part) => part !== '').join(', ');
      succeed(geocoded.coords, place === '' ? geocoded.city : place, cep);
    },
    [fail, geocode, succeed],
  );

  const useLocation = useCallback(async () => {
    setState('busy');
    track('sf_geolocation_used');
    try {
      const coords = await geolocate();
      track('sf_location_submitted', {
        input_type: 'geolocation',
        latitude: roundCoord(coords.lat),
        longitude: roundCoord(coords.lon),
      });
      succeed(coords, '', null);
    } catch (error) {
      const denied = !isPositionError(error) || error.code === PERMISSION_DENIED;
      fail(denied ? 'geolocation_denied' : 'geolocation_unavailable');
    }
  }, [fail, geolocate, succeed]);

  const reset = useCallback(() => {
    setResult(null);
    setErrorKind(null);
    setState('idle');
  }, []);

  return { state, result, errorKind, submitCep, useLocation, reset };
};
